import { UnitLabel } from '@/shared/config/config'
import {
  InputGroup,
  Button,
  Card,
  CardTitle,
  CardBody,
  CardHeader,
  DataBlock,
  NumericInputGroup,
  isValidPercentage
} from '@/common'
import Drawer from '@/shared/components/ui/Drawer'
import calculatePFCEmissionsUsingSlopeMethod from '@/features/reports/formulas/calculatePFCmissionsUsingSlopeMethod'
import { Artifacts } from '@/api/EmissionReportModel'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useI18n from '@/shared/hooks/useI18n'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { gwpcf4, slopeMethodConstants, gwpc2f6 } from '../constants'

export default function SlopeMethodCalculatorPanel({
  isOpen,
  close,
  addEmission,
  assetId,
  aluminiumQuantity,
  initialValues
}: {
  isOpen: boolean
  close: () => void
  addEmission: (emission: number, artifacts: Artifacts) => void
  assetId: string
  aluminiumQuantity: number
  initialValues?: Artifacts
}) {
  const { t, numberFormatter3Decimals } = useI18n()
  const { checkPermission } = usePermissions()
  const canEditReport = checkPermission('emissionReport', 'update')
  const constants = slopeMethodConstants[assetId as keyof typeof slopeMethodConstants]
  const [state, setState] = useState({
    frequency: initialValues?.frequency || '',
    averageDuration: initialValues?.averageDuration || '',
    cf4EmissionFactor: initialValues?.cf4EmissionFactor || constants?.cf4EmissionFactor || '',
    c2f6EmissionFactor: initialValues?.c2f6EmissionFactor || constants?.c2f6EmissionFactor || '',
    gwpcf4: initialValues?.gwpcf4 || gwpcf4 || '',
    gwpc2f6: initialValues?.gwpc2f6 || gwpc2f6 || '',
    collectionEfficiency: initialValues?.collectionEfficiency || '100',
    aluminiumQuantityTonnes: aluminiumQuantity
  })

  const onValueChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, [evt.target.name]: evt.target.value }))
  }

  const emission = calculatePFCEmissionsUsingSlopeMethod({
    frequency: Number(state.frequency),
    averageDuration: Number(state.averageDuration),
    cf4EmissionFactor: Number(state.cf4EmissionFactor),
    c2f6EmissionFactor: Number(state.c2f6EmissionFactor),
    aluminiumQuantityTonnes: aluminiumQuantity,
    gwpcf4: Number(state.gwpcf4),
    gwpc2f6: Number(state.gwpc2f6),
    collectionEfficiency: Number(state.collectionEfficiency)
  })

  const isAddEnabled = useMemo(() => {
    return emission && emission > 0
  }, [emission])

  const onAddClick = useCallback(() => {
    if (!isAddEnabled) {
      return
    }
    const artifacts = state
    addEmission(emission, artifacts)
    close()
  }, [addEmission, close, emission, isAddEnabled, state])

  useEffect(() => {
    if (isOpen && initialValues) {
      setState(prev => ({ ...prev, ...initialValues, aluminiumQuantityTonnes: aluminiumQuantity }))
    }
  }, [isOpen, initialValues])

  const formattedEmission = useMemo(() => {
    const emissionExistsAndNotInfinite = emission && emission !== Infinity
    const formatted = emissionExistsAndNotInfinite ? `${numberFormatter3Decimals(emission)}` : '0'
    return `${formatted} ${UnitLabel.tCO2e}`
  }, [emission, numberFormatter3Decimals])

  return (
    <Drawer isOpen={isOpen} close={close} sizeType="3xl">
      <Card>
        <CardHeader>
          <CardTitle title={t('report:calculators.slopeMethodCalculatorPanel.title')} />
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-2 gap-4 items-end">
            <div className="col-span-2">
              <NumericInputGroup
                numericFormat={{
                  decimalScale: 3,
                  allowNegative: false
                }}
                name="frequency"
                label={t('report:calculators.slopeMethodCalculatorPanel.fields.frequency.label')}
                value={state.frequency}
                suffix={t('report:calculators.slopeMethodCalculatorPanel.fields.frequency.suffix')}
                onChange={onValueChange}
              />
            </div>
            <div className="col-span-2">
              <NumericInputGroup
                numericFormat={{
                  decimalScale: 3,
                  allowNegative: false
                }}
                name="averageDuration"
                label={t(
                  'report:calculators.slopeMethodCalculatorPanel.fields.averageDuration.label'
                )}
                value={state.averageDuration}
                suffix={t(
                  'report:calculators.slopeMethodCalculatorPanel.fields.averageDuration.suffix'
                )}
                onChange={onValueChange}
              />
            </div>

            <div className="col-span-2">
              <NumericInputGroup
                numericFormat={{
                  decimalScale: 0,
                  allowNegative: false,
                  isAllowed: isValidPercentage
                }}
                name="collectionEfficiency"
                suffix={UnitLabel.percent}
                onChange={onValueChange}
                value={state.collectionEfficiency}
                label={t(
                  'report:calculators.slopeMethodCalculatorPanel.fields.collectionEfficiency.label'
                )}
              />
            </div>

            <NumericInputGroup
              numericFormat={{
                decimalScale: 3,
                allowNegative: false
              }}
              name="cf4EmissionFactor"
              label={t(
                'report:calculators.slopeMethodCalculatorPanel.fields.cf4EmissionFactor.label'
              )}
              value={state.cf4EmissionFactor}
              onChange={onValueChange}
              suffix="(kg CF4/t Al)/(AE-Mins/ cell-day)"
            />

            <NumericInputGroup
              numericFormat={{
                decimalScale: 3
              }}
              name="c2f6EmissionFactor"
              label={t(
                'report:calculators.slopeMethodCalculatorPanel.fields.c2f6EmissionFactor.label'
              )}
              value={state.c2f6EmissionFactor}
              onChange={onValueChange}
              suffix="t C2F6/t CF4"
            />

            <InputGroup
              name="gwpcf4"
              label={t('report:calculators.slopeMethodCalculatorPanel.fields.gwpcf4.label')}
              value={state.gwpcf4}
              disabled
              suffix="tCO2e/tCF4"
            />

            <InputGroup
              name="gwpc2f6"
              label={t('report:calculators.slopeMethodCalculatorPanel.fields.gwpc2f6.label')}
              value={state.gwpc2f6}
              disabled
              suffix="tCO2e/tC2F6"
            />
          </div>

          <DataBlock label={t('report:common:emission')} data={formattedEmission} />
        </CardBody>
        <footer className="grid grid-cols-2 gap-4 px-4 pb-4">
          <Button variant="subtle" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={!isAddEnabled || !canEditReport} onClick={onAddClick}>
            {t('common:actions.add')}
          </Button>
        </footer>
      </Card>
    </Drawer>
  )
}
